import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  FormControlLabel,
  Switch,
  Stack,
} from "@mui/material";
import MDButton from "components/MDButton";
import { useTranslation } from "react-i18next";

const AddStructureComponentDialog = ({ open, onClose, onSubmit, availableComponents }) => {
  const { t } = useTranslation();
  const [salaryComponentId, setSalaryComponentId] = useState("");
  const [amount, setAmount] = useState("");
  const [percentage, setPercentage] = useState("");
  const [isMandatory, setIsMandatory] = useState(true);

  const selectedComponent = availableComponents.find((c) => c.id === salaryComponentId);
  const isPercentageBased = selectedComponent?.calculationType === "percentage";

  const handleClose = () => {
    setSalaryComponentId("");
    setAmount("");
    setPercentage("");
    setIsMandatory(true);
    onClose();
  };

  const handleSubmit = () => {
    onSubmit({
      salaryComponentId,
      amount: isPercentageBased ? null : Number(amount),
      percentage: isPercentageBased ? Number(percentage) : null,
      isMandatory,
      displayOrder: 0,
    });
    handleClose();
  };

  const canSubmit = !!salaryComponentId && (isPercentageBased ? percentage !== "" : amount !== "");

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: "bold" }}>
        {t("admin.payroll.structures.addComponentTitle")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Autocomplete
            options={availableComponents}
            getOptionLabel={(c) => `${c.name} (${c.code})`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={availableComponents.find((c) => c.id === salaryComponentId) || null}
            onChange={(event, newValue) => setSalaryComponentId(newValue?.id || "")}
            renderInput={(params) => (
              <TextField {...params} label={t("admin.payroll.structures.component")} />
            )}
          />

          {isPercentageBased ? (
            <TextField
              label={t("admin.payroll.structures.percentage")}
              type="number"
              value={percentage}
              onChange={(e) => setPercentage(e.target.value)}
              fullWidth
              helperText={t("admin.payroll.structures.percentageHelper")}
            />
          ) : (
            <TextField
              label={t("admin.payroll.structures.amount")}
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              fullWidth
              disabled={!salaryComponentId}
            />
          )}

          <FormControlLabel
            control={
              <Switch checked={isMandatory} onChange={(e) => setIsMandatory(e.target.checked)} />
            }
            label={t("admin.payroll.structures.mandatory")}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton onClick={handleClose} color="secondary">
          {t("common.cancel")}
        </MDButton>
        <MDButton onClick={handleSubmit} color="primary" disabled={!canSubmit}>
          {t("common.add")}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

AddStructureComponentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  availableComponents: PropTypes.array,
};

AddStructureComponentDialog.defaultProps = {
  availableComponents: [],
};

export default AddStructureComponentDialog;
